import React, { useState, useEffect } from 'react';
import './InventoryValue.css';

function InventoryValue({ stockData }) {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [totalInventoryValue, setTotalInventoryValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('sku');
  const [sortDirection, setSortDirection] = useState('asc');

  // Charger les prix unitaires sauvegardés lors du démarrage du composant
  useEffect(() => {
    const savedPrices = localStorage.getItem('inventoryPrices');
    const savedSuppliesStock = localStorage.getItem('suppliesStock');
    
    let allItems = [];
    
    // Ajouter les données de stock marchandises
    if (stockData && stockData.length > 0) {
      const dataWithTotalQuantity = stockData.map(item => {
        let totalQuantity;
        
        if (item.quantity !== undefined) {
          totalQuantity = item.quantity;
        } else {
          totalQuantity = (parseInt(item.fbmQuantity) || 0) + 
                          (parseInt(item.fbaQuantity) || 0) + 
                          (parseInt(item.homeQuantity) || 0) + 
                          (parseInt(item.transitQuantity) || 0);
        }
        
        return {
          ...item,
          quantity: totalQuantity,
          type: 'marchandise'
        };
      });
      
      allItems = [...dataWithTotalQuantity];
    }
    
    // Ajouter les données de stock fournitures
    if (savedSuppliesStock) {
      try {
        const suppliesData = JSON.parse(savedSuppliesStock);
        const suppliesWithQuantity = suppliesData.map(item => ({
          sku: `FOURNITURE-${item.id}`, // Préfixe pour éviter les conflits de SKU
          productName: item.name,
          quantity: (item.homeQuantity || 0) + (item.transitQuantity || 0),
          homeQuantity: item.homeQuantity || 0,
          transitQuantity: item.transitQuantity || 0,
          fbmQuantity: 0,
          fbaQuantity: 0,
          type: 'fourniture',
          originalId: item.id // Garder l'ID original pour la sauvegarde des prix
        }));
        
        allItems = [...allItems, ...suppliesWithQuantity];
      } catch (e) {
        console.error('Erreur lors du chargement du stock fournitures :', e);
      }
    }
    
    if (allItems.length > 0) {
      // Récupérer les prix unitaires sauvegardés
      const parsedPrices = savedPrices ? JSON.parse(savedPrices) : {};
      
      // Initialiser les données d'inventaire avec les prix sauvegardés
      const itemsWithPrices = allItems.map(item => {
        // Pour les fournitures, utiliser l'ID original comme clé
        const priceKey = item.type === 'fourniture' ? `FOURNITURE-${item.originalId}` : item.sku;
        const unitPrice = parsedPrices[priceKey] || 0;
        
        return {
          ...item,
          priceKey: priceKey, // Sauvegarder la clé pour les prix
          unitPrice: unitPrice,
          stockValue: item.quantity * unitPrice
        };
      });
      
      setInventoryItems(itemsWithPrices);
      calculateTotalValue(itemsWithPrices);
    } else {
      setInventoryItems([]);
      setTotalInventoryValue(0);
    }
  }, [stockData]);

  // Calculer la valeur totale de l'inventaire
  const calculateTotalValue = (items) => {
    const total = items.reduce((sum, item) => sum + (item.stockValue || 0), 0);
    setTotalInventoryValue(total);
  };

  // Gérer la modification du prix unitaire
  const handleUnitPriceChange = (priceKey, price) => {
    // Convertir le prix en nombre
    const numericPrice = parseFloat(price) || 0;
    
    // Mettre à jour les éléments d'inventaire
    const updatedItems = inventoryItems.map(item => {
      if (item.priceKey === priceKey) {
        const quantity = item.quantity || 0;
        return {
          ...item,
          unitPrice: numericPrice,
          stockValue: quantity * numericPrice
        };
      }
      return item;
    });
    
    setInventoryItems(updatedItems);
    calculateTotalValue(updatedItems);
    
    // Sauvegarder les prix unitaires dans le localStorage
    const currentPrices = {};
    updatedItems.forEach(item => {
      currentPrices[item.priceKey] = item.unitPrice;
    });
    
    localStorage.setItem('inventoryPrices', JSON.stringify(currentPrices));
  };

  // Gestion du tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filtrer les données selon le terme de recherche
  const filteredData = inventoryItems.filter(item => {
    const term = searchTerm.toLowerCase();
    return (
      (item.sku && item.sku.toLowerCase().includes(term)) ||
      (item.productName && item.productName.toLowerCase().includes(term)) ||
      (item.type && item.type.toLowerCase().includes(term))
    );
  });

  // Trier les données
  const sortedData = [...filteredData].sort((a, b) => {
    let valueA = a[sortField];
    let valueB = b[sortField];
    
    // Convertir en nombres pour les champs numériques
    if (['quantity', 'unitPrice', 'stockValue'].includes(sortField)) {
      valueA = parseFloat(valueA) || 0;
      valueB = parseFloat(valueB) || 0;
    } else if (typeof valueA === 'string' && typeof valueB === 'string') {
      valueA = valueA.toLowerCase();
      valueB = valueB.toLowerCase();
    }
    
    if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Exporter l'inventaire au format CSV
  const exportCSV = () => {
    // Préparer les en-têtes
    const headers = ['SKU', 'Produit', 'Type', 'Quantité', 'Prix unitaire (€)', 'Valeur du stock (€)'];
    
    // Préparer les lignes de données
    const dataRows = sortedData.map(item => [
      item.sku || '',
      item.productName || '',
      item.type === 'marchandise' ? 'Marchandise' : 'Fourniture',
      item.quantity || 0,
      item.unitPrice.toFixed(2).replace('.', ','),
      item.stockValue.toFixed(2).replace('.', ',')
    ]);
    
    // Calculer les totaux par type
    const marchandiseTotal = sortedData
      .filter(item => item.type === 'marchandise')
      .reduce((sum, item) => sum + item.stockValue, 0);
    
    const fournitureTotal = sortedData
      .filter(item => item.type === 'fourniture')
      .reduce((sum, item) => sum + item.stockValue, 0);
    
    // Ajouter les lignes de totaux
    dataRows.push(['', '', '', '', '', '']);
    dataRows.push(['', 'TOTAL MARCHANDISES', '', '', '', marchandiseTotal.toFixed(2).replace('.', ',')]);
    dataRows.push(['', 'TOTAL FOURNITURES', '', '', '', fournitureTotal.toFixed(2).replace('.', ',')]);
    dataRows.push(['', 'TOTAL GÉNÉRAL', '', '', '', totalInventoryValue.toFixed(2).replace('.', ',')]);
    
    // Combiner les en-têtes et les données
    const csvContent = [
      headers.join(';'),
      ...dataRows.map(row => row.join(';'))
    ].join('\n');
    
    // Créer un blob et un lien de téléchargement
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    // Configurer le lien de téléchargement
    const date = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.setAttribute('download', `inventaire-comptable-${date}.csv`);
    
    // Déclencher le téléchargement
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculer les totaux par type pour l'affichage
  const marchandiseTotal = inventoryItems
    .filter(item => item.type === 'marchandise')
    .reduce((sum, item) => sum + item.stockValue, 0);
  
  const fournitureTotal = inventoryItems
    .filter(item => item.type === 'fourniture')
    .reduce((sum, item) => sum + item.stockValue, 0);

  // Si pas de données, afficher un message
  if (inventoryItems.length === 0) {
    return <p>Aucune donnée disponible pour l'inventaire comptable</p>;
  }

  return (
    <div className="inventory-value-container">
      <h2>Inventaire Comptable</h2>
      
      <div className="inventory-controls">
        <input
          type="text"
          placeholder="Rechercher par SKU, nom du produit ou type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="inventory-search"
        />
        
        <button 
          onClick={exportCSV}
          className="export-button"
        >
          Exporter en CSV
        </button>
      </div>
      
      <div className="table-wrapper">
        <table className="inventory-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('sku')}>
                SKU {sortField === 'sku' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('productName')}>
                Produit {sortField === 'productName' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('type')}>
                Type {sortField === 'type' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('quantity')}>
                Quantité Totale {sortField === 'quantity' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('unitPrice')}>
                Prix Unitaire (€) {sortField === 'unitPrice' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('stockValue')}>
                Valeur du Stock (€) {sortField === 'stockValue' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => (
              <tr key={index} className={`inventory-row ${item.type}`}>
                <td>{item.sku}</td>
                <td>{item.productName ? (item.productName.length > 55 ? item.productName.substring(0, 55) + '...' : item.productName) : ''}</td>
                <td>
                  <span className={`type-badge ${item.type}`}>
                    {item.type === 'marchandise' ? 'Marchandise' : 'Fourniture'}
                  </span>
                </td>
                <td>{item.quantity || 0}</td>
                <td className="price-cell">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.unitPrice || 0}
                    onChange={(e) => handleUnitPriceChange(item.priceKey, e.target.value)}
                    className="price-input"
                  />
                </td>
                <td className="stock-value">{item.stockValue.toFixed(2)} €</td>
              </tr>
            ))}
            <tr className="total-row subtotal">
              <td colSpan="5" className="total-label">Valeur Totale Marchandises</td>
              <td className="total-value">{marchandiseTotal.toFixed(2)} €</td>
            </tr>
            <tr className="total-row subtotal">
              <td colSpan="5" className="total-label">Valeur Totale Fournitures</td>
              <td className="total-value">{fournitureTotal.toFixed(2)} €</td>
            </tr>
            <tr className="total-row final-total">
              <td colSpan="5" className="total-label">Valeur Totale de l'Inventaire</td>
              <td className="total-value">{totalInventoryValue.toFixed(2)} €</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default InventoryValue;